import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import Menu from "@mui/material/Menu";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CorporateFareRoundedIcon from "@mui/icons-material/CorporateFareRounded";
import React from "react";
import useUser from "../../../providers/user/user_provider";
import { useNavigate } from "react-router-dom";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../../../constants";
import { adminLogout } from "../../../services/logout/logout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { purple, purpleLightHover, black } from "../../../theme/colors";
import LogoutConfirmDialog from "../../../components/common/logout_confirm_dialog";

export default function Profile() {
  const { userPermissions, switchOrg } = useUser();
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // Check if user is a merchant
  const isMerchant = () => {
    const merchantData = sessionStorage.getItem('merchant_data');
    return merchantData !== null;
  };

  // Get user data (admin or merchant)
  const getUserData = () => {
    if (isMerchant()) {
      const merchantData = JSON.parse(sessionStorage.getItem('merchant_data') || '{}');
      return {
        name: merchantData.name || 'Merchant',
        email: merchantData.mobile_no || 'merchant@example.com'
      };
    }
    return {
      name: userPermissions?.name || 'Admin',
      email: userPermissions?.emailId || 'admin@example.com'
    };
  };

  const userData = getUserData();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      if (isMerchant()) {
        // Merchant logout - no backend session to invalidate (merchants
        // authenticate via a short-lived OTP token, not a server-tracked
        // session), so this is local-only cleanup, same as
        // merchant_drawer.tsx's logout handler.
        sessionStorage.removeItem('merchant_data');
        showSnackbar("Logged out successfully");
        navigate("/");
      } else {
        // Admin logout
        const res = await adminLogout();
        if (res.status === 200) {
          window.location.href = res.data.data.redirectUrl;
        }
      }

      // Clear tokens
      localStorage.setItem(ACCESS_TOKEN, "");
      localStorage.setItem(REFRESH_TOKEN, "");
      sessionStorage.setItem(ACCESS_TOKEN, "");
      sessionStorage.setItem(REFRESH_TOKEN, "");
    } catch (error) {
      console.error("Logout error:", error);
      showSnackbar("Logout failed");
    } finally {
      setIsLoggingOut(false);
      setLogoutDialogOpen(false);
    }
  };

  return (
    <Box>
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: 3,
          textTransform: "none",
          "&:hover": { bgcolor: purpleLightHover },
        }}
      >
        <Avatar sx={{ bgcolor: purple, width: 36, height: 36, fontSize: 15 }}>
          {userData.name?.[0]}
        </Avatar>
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            alignItems: "flex-start",
            lineHeight: 1.1,
          }}
        >
          <Typography fontSize={13.5} fontWeight={600} color={black}>
            {userData.name}
          </Typography>
          {!isMerchant() && userPermissions?.orgName && (
            <Typography fontSize={11} fontWeight={700} color={purple} mt={0.25}>
              {userPermissions.orgName}
            </Typography>
          )}
          <Typography fontSize={11} color="#7C797C">
            {isMerchant() ? "Merchant" : userPermissions?.role || "Admin"}
          </Typography>
        </Box>
        <ArrowDropDownIcon sx={{ color: "#7C797C" }} />
      </Button>
      <Menu
        elevation={3}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{
          sx: { p: 0, m: 0, mt: 1, borderRadius: 2.5, overflow: "hidden" },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <List dense sx={{ width: 300, p: 0, m: 0 }}>
          <ListItem sx={{ bgcolor: purpleLightHover, py: 2 }}>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: purple }}>
                {userData.name?.[0]}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primaryTypographyProps={{ color: black, fontWeight: 700 }}
              secondaryTypographyProps={{ color: "#7C797C" }}
              primary={userData.name}
              secondary={userData.email}
            ></ListItemText>
          </ListItem>
          {!isMerchant() && (userPermissions?.availableOrgs?.length ?? 0) > 1 && (
            <>
              <Divider />
              <ListItem sx={{ pb: 0.5, pt: 1.5 }}>
                <Typography fontSize={11} fontWeight={700} color="#7C797C" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Switch organisation
                </Typography>
              </ListItem>
              {userPermissions?.availableOrgs?.map((org) => (
                <ListItemButton
                  key={org.orgId}
                  onClick={() => {
                    handleClose();
                    if (org.orgId !== userPermissions?.orgId) {
                      switchOrg(org.orgId);
                    }
                  }}
                  sx={{ py: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: purpleLightHover, color: purple, width: 32, height: 32 }}>
                      <CorporateFareRoundedIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{
                      color: black,
                      fontWeight: org.orgId === userPermissions?.orgId ? 700 : 500,
                      fontSize: 14,
                    }}
                    primary={org.orgName ?? `Org #${org.orgId}`}
                  />
                  {org.orgId === userPermissions?.orgId && (
                    <CheckRoundedIcon sx={{ color: purple, fontSize: 18 }} />
                  )}
                </ListItemButton>
              ))}
            </>
          )}
          <Divider />
          <ListItem sx={{ py: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              startIcon={<LogoutRoundedIcon />}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
              onClick={() => {
                handleClose();
                setLogoutDialogOpen(true);
              }}
            >
              Logout
            </Button>
          </ListItem>
        </List>
      </Menu>
      <LogoutConfirmDialog
        open={logoutDialogOpen}
        isLoggingOut={isLoggingOut}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
      />
    </Box>
  );
}
